/*
 * Copyright (c) 2021 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import {
    EntirePeriodTurnover,
    RegistryTurnoversBase,
    RegistryTurnoversByCashBox,
    RegistryTurnoversByObject,
    TurnoversByPeriodGroupType,
} from '../../../data/api/apiCommon.typings';
import { RouteId, RouteParam } from '../../../router/routes';
import { toLocaleString } from '../../../data/translation/i18n';
import { TurnoversColumnConfig } from './ListTypes';
import { checkValue } from 'utils/utils';
import { TOP_BAR_HEIGHT } from '../../TopBar/TopBar';
import { theme } from '../../../assets/style/them';
import { PAGINATION_HEIGHT } from './ListStyles';

const TURNOVERS_CAPTION_HEIGHT = 78;
export const TABLE_HEIGHT_SUBTR =
    TOP_BAR_HEIGHT + PAGINATION_HEIGHT + TURNOVERS_CAPTION_HEIGHT + parseInt(theme.spacing(5));

export interface TurnoversTableData {
    period?: string;
    group?: TurnoversByPeriodGroupType;
    expandMore?: boolean;
    objectName?: string;
    objectTaxNumber?: string;
    cashBoxTaxNumber?: string;

    type?: string;

    salesSum: string;
    salesCount: string;
    moneybacksSum: string;
    moneybacksCount: string;
    rollbacksSum: string;
    rollbacksCount: string;
    cash: string;
    cashless: string;
    other: string;
    tax: string;
    turnover: string;
    currency: string;
}

const createBaseTurnoversTableData = (data: RegistryTurnoversBase): TurnoversTableData => {
    return {
        salesSum: toLocaleString(data.sales.sum + data.corrections.sum),
        salesCount: toLocaleString(data.sales.count + data.corrections.count),
        moneybacksSum: toLocaleString(data.moneybacks.sum),
        moneybacksCount: toLocaleString(data.moneybacks.count),
        rollbacksSum: toLocaleString(data.rollbacks.sum),
        rollbacksCount: toLocaleString(data.rollbacks.count),
        cash: toLocaleString(data.cash),
        cashless: toLocaleString(data.cashless),
        other: toLocaleString(data.bonus + data.prepayment + data.credit),
        tax: toLocaleString(data.vatSum),
        turnover: toLocaleString(data.turnover),
        currency: data.currency,
    };
};

export const createTurnoversByObjectsTableData = (turnovers: RegistryTurnoversByObject[]) => {
    return turnovers.reduce((tableData, turnover) => {
        const dataItem: Partial<TurnoversTableData> = createBaseTurnoversTableData(turnover);

        dataItem.objectTaxNumber = checkValue(turnover.taxNumber);
        dataItem.objectName = checkValue(turnover.name);

        tableData.push(dataItem);

        return tableData;
    }, []);
};

export const createTurnoversByCashBoxesTableData = (turnovers: RegistryTurnoversByCashBox[]) => {
    return turnovers.reduce((tableData, turnover) => {
        const dataItem: Partial<TurnoversTableData> = createBaseTurnoversTableData(turnover);

        dataItem.cashBoxTaxNumber = checkValue(turnover.taxNumber);

        tableData.push(dataItem);
        return tableData;
    }, []);
};

const combinePeriodToDisplay = (year: string, month: string): string => {
    return year + '.' + month;
};

export const splitDisplayedPeriod = (period: string): string[] => {
    return period.split('.');
};

export const createTurnoversByPeriodTableData = (turnovers: EntirePeriodTurnover) => {
    return turnovers.yearsTurnovers.reduce((yearsTableData, yearTurnover) => {
        const yearDataItem: Partial<TurnoversTableData> = createBaseTurnoversTableData(yearTurnover.yearSum);
        yearDataItem.period = yearTurnover.year;
        yearDataItem.group = TurnoversByPeriodGroupType.YEAR;
        yearDataItem.expandMore = yearTurnover.isHiddenMonthsList;
        yearsTableData.push(yearDataItem);

        if (!yearTurnover.isHiddenMonthsList) {
            const yearByMonthsTableData = yearTurnover.monthsTurnovers.reduce((monthsTableData, monthTurnover) => {
                const monthDataItem: Partial<TurnoversTableData> = createBaseTurnoversTableData(monthTurnover.monthSum);
                monthDataItem.period = combinePeriodToDisplay(yearTurnover.year, monthTurnover.month);
                monthDataItem.group = TurnoversByPeriodGroupType.MONTH;
                monthDataItem.expandMore = monthTurnover.isHiddenDaysList;
                monthsTableData.push(monthDataItem);

                if (!monthTurnover.isHiddenDaysList) {
                    const monthByDaysTableData = monthTurnover.daysTurnovers.reduce((daysTableData, dayTurnover) => {
                        const dayDataItem: Partial<TurnoversTableData> = createBaseTurnoversTableData(
                            dayTurnover.daySum,
                        );
                        dayDataItem.period = dayTurnover.day;
                        dayDataItem.group = TurnoversByPeriodGroupType.DAY;
                        daysTableData.push(dayDataItem);
                        return daysTableData;
                    }, []);
                    monthsTableData = monthsTableData.concat(monthByDaysTableData);
                }

                return monthsTableData;
            }, []);
            yearsTableData = yearsTableData.concat(yearByMonthsTableData);
        }

        return yearsTableData;
    }, []);
};

export const turnoversColumns: TurnoversColumnConfig[] = [
    {
        keyForValue: 'type',
        translatePath: '',
        align: 'left',
        minWidth: 200,
    },
    {
        keyForValue: 'salesSum',
        translatePath: 'app.sum',
        align: 'right',
        minWidth: 100,
    },
    {
        keyForValue: 'salesCount',
        translatePath: 'app.quantity',
        align: 'right',
        minWidth: 100,
    },
    {
        keyForValue: 'moneybacksSum',
        translatePath: 'app.sum',
        align: 'right',
        minWidth: 100,
    },
    {
        keyForValue: 'moneybacksCount',
        translatePath: 'app.quantity',
        align: 'right',
        minWidth: 100,
    },
    {
        keyForValue: 'rollbacksSum',
        translatePath: 'app.sum',
        align: 'right',
        minWidth: 100,
    },
    {
        keyForValue: 'rollbacksCount',
        translatePath: 'app.quantity',
        align: 'right',
        minWidth: 100,
    },
    {
        keyForValue: 'cash',
        translatePath: 'document.paymentTypesList.cash',
        align: 'right',
    },
    {
        keyForValue: 'cashless',
        translatePath: 'document.paymentTypesList.cashless',
        align: 'right',
    },
    {
        keyForValue: 'other',
        translatePath: 'app.other',
        align: 'right',
    },
    {
        keyForValue: 'tax',
        translatePath: 'document.tax',
        align: 'right',
    },
    {
        keyForValue: 'turnover',
        translatePath: 'domain.turnover',
        align: 'right',
    },
    {
        keyForValue: 'currency',
        translatePath: 'domain.currency',
        align: 'center',
        maxWidth: 100,
    },
];

export const turnoversByPeriodColumns = [...turnoversColumns];
turnoversByPeriodColumns.splice(0, 1, {
    keyForValue: 'period',
    translatePath: 'domain.period',
    minWidth: 200,
});

export const turnoversByObjectsColumns = [...turnoversColumns];
turnoversByObjectsColumns.splice(
    0,
    1,
    {
        keyForValue: 'objectName',
        translatePath: 'domain.store',
        isWithTooltip: true,
        link: {
            routeId: RouteId.RegistryStoreCard,
            routeParamName: RouteParam.StoreTaxNumber,
            routeParamKeyForValue: 'objectTaxNumber',
        },
        isBold: true,
        align: 'left',
        minWidth: 200,
    },
    {
        keyForValue: 'objectTaxNumber',
        translatePath: '',
        isHidden: true, //not shown, but used for link
    },
);

export const turnoversByCashBoxesColumns = [...turnoversColumns];
turnoversByCashBoxesColumns.splice(0, 1, {
    keyForValue: 'cashBoxTaxNumber',
    translatePath: 'domain.cashbox',
    link: {
        routeId: RouteId.RegistryCashboxCard,
        routeParamName: RouteParam.CashboxTaxNumber,
        routeParamKeyForValue: 'cashBoxTaxNumber',
    },
    align: 'left',
    isBold: true,
    minWidth: 200,
});
